import { PositionWithIdentity } from "./positionIdentity";

// Local trade signal bus.
//
// Why this exists: the Position and Open Orders tabs are backed by
// `useSWRInfinite` (see `useCursorPaginatedHistory`). SWR's key-filter form of
// `mutate` — `mutate(key => ...)` — deliberately SKIPS every `$inf$` key:
//
//   // Skip the special useSWRInfinite and useSWRSubscription keys.
//   !/^\$(inf|sub)\$/.test(key) && keyFilter(cache.get(key)._k)
//
// so a broad key-filter sweep could refresh `/account` and `/portfolio` but
// could NEVER refresh `/positions`. That sweep has since been removed entirely:
// this bus is now the whole mechanism. An action announces a confirmed trade
// here, and each data owner subscribes and converges on its own evidence,
// refetching until its payload actually changes rather than firing a fixed
// ladder across every endpoint.

export interface TradeExecutedDetail {
  /**
   * The position a confirmed FULL close just wound down. Subscribers drop the
   * row (and tombstone its identity) straight away instead of waiting for the
   * indexer to stop returning it — a close is the one case where the frontend
   * already knows the final answer, so the row should never linger.
   *
   * Omitted for opens, partial closes and cancels, where only a refetch can
   * tell us the new state.
   */
  closedPosition?: PositionWithIdentity;

  /**
   * How strongly the close is evidenced.
   *
   * `receipt` waited for the chain (`tx.wait`). `broadcast` only saw a relayer
   * accept the transaction and never waited at all, so it can still revert.
   * NEITHER proves the position reached zero: the router turns a close into a
   * reduce-only market order against the CLOB, so a thin book can leave a
   * remainder. Subscribers must treat both as provisional.
   */
  closeProof?: "receipt" | "broadcast";

  /**
   * A position the trader just OPENED or increased.
   *
   * This is how a re-open on a market and side that was closed moments ago
   * releases the pending close. Field divergence (a changed size or entry
   * price) catches most re-opens, but a position re-opened at the same size and
   * price is indistinguishable from the stale row — and our own confirmed
   * action is direct evidence, not an inference.
   */
  openedPosition?: PositionWithIdentity;
}

type TradeListener = (detail: TradeExecutedDetail) => void;

const listeners = new Set<TradeListener>();

/** Fire after a trade is confirmed on-chain (open / close / cancel). */
export const emitTradeExecuted = (detail: TradeExecutedDetail = {}): void => {
  listeners.forEach((listener) => {
    try {
      listener(detail);
    } catch {
      // A misbehaving subscriber must not stop the others from refreshing.
    }
  });
};

/** Subscribe to local trade confirmations. Returns an unsubscribe function. */
export const subscribeTradeExecuted = (listener: TradeListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
